import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { fonts, colors, size } from '../../ColorFont/color';
import { IconPlus, IconCube, IconBolt, IconLeaf, IconChevronRight, IconBox, IconMapPin, IconClock } from '@tabler/icons-react';
import NewHeaderNavWithSidebar from '../../components/HeaderNav/NewHeaderNavWithSidebar';
import { url } from '../../ConstUrl/Url';
import { CircularProgress, Grid } from '@mui/material';
import UnifiedCropCard from '../../components/UnifiedCropCard';
import CropDetailsModal from '../../components/CropDetailsModal';

const farmerTranslations = {
    en: {
        welcomeBack: "Welcome Back",
        manageProduce: "Manage your produce listings",
        sellCrop: "Sell Your Crop",
        myListings: "My Listings",
        boostPlans: "Boost Plans",
        carbonCredits: "Carbon Credits",
        boostTextTitle: "Boost Your Listings",
        boostTextSub: "Get 5x more views and sell faster!",
        viewPlans: "View Plans",
        viewAll: "View All",
        noListingsYet: "No listings yet",
        startPosting: "Start by posting your first produce",
        addCrop: "Add Your Crop"
    },
    hi: {
        welcomeBack: "वापसी पर स्वागत है",
        manageProduce: "अपनी उपज सूची प्रबंधित करें",
        sellCrop: "अपनी फसल बेचे",
        myListings: "मेरी लिस्टिंग",
        boostPlans: "बूस्ट प्लान",
        carbonCredits: "कार्बन क्रेडिट",
        boostTextTitle: "अपनी सूचियों को बूस्ट करें",
        boostTextSub: "5x अधिक दृश्य प्राप्त करें और तेजी से बेचें!",
        viewPlans: "प्लान्स देखें",
        viewAll: "सभी देखें",
        noListingsYet: "अभी तक कोई सूची नहीं",
        startPosting: "अपनी पहली उपज पोस्ट करके शुरुआत करें",
        addCrop: "अपनी फसल जोड़ें"
    },
    mr: {
        welcomeBack: "परत आल्याबद्दल स्वागत आहे",
        manageProduce: "तुमची उत्पादन यादी व्यवस्थापित करा",
        sellCrop: "तुमचे पीक विका",
        myListings: "माझ्या सूची",
        boostPlans: "बूस्ट योजना",
        carbonCredits: "कार्बन क्रेडिट्स",
        boostTextTitle: "तुमच्या याद्या बूस्ट करा",
        boostTextSub: "5x अधिक दृश्ये मिळवा आणि जलद विक्री करा!",
        viewPlans: "योजना पहा",
        viewAll: "सर्व पहा",
        noListingsYet: "अद्याप कोणत्याही याद्या नाहीत",
        startPosting: "तुमचे पहिले उत्पादन पोस्ट करून सुरुवात करा",
        addCrop: "तुमचे पीक जोडा"
    }
};

const ActionCard = ({ icon: Icon, iconBg, title, onClick }) => (
    <Box
        onClick={onClick}
        sx={{
            flex: { xs: '1 1 calc(50% - 5px)', sm: '1 1 calc(50% - 6px)', md: '1 1 0' },
            maxWidth: { xs: 'calc(50% - 5px)', sm: 'calc(50% - 6px)', md: '100%' },
            height: '112px',
            backgroundColor: colors.white_bg || '#FFFFFF',
            borderRadius: '24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            cursor: 'pointer',
            border: `1px solid ${colors.dashboard_card_border || '#E0E5EB'}`,
            boxShadow: colors.action_box_shadow,
            transition: 'all 0.2s',
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: colors.shadow_card,
            }
        }}
    >
        <Box sx={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#FFFFFF'
        }}>
            <Icon size={24} stroke={2} />
        </Box>
        <Typography sx={{
            fontFamily: fonts.poppins,
            fontSize: '16px',
            fontWeight: 500,
            color: colors.text_dark || '#000000',
            lineHeight: '24px',
            textAlign: 'center'
        }}>
            {title}
        </Typography>
    </Box>
);

// CropCardMini removed in favor of UnifiedCropCard

const FarmerDashboard = ({ userData, onAddCropClick, onViewPlansClick, onViewAllClick, onActionClick }) => {
    const { i18n } = useTranslation();
    const [crops, setCrops] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [selectedCrop, setSelectedCrop] = React.useState(null);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = React.useState(false);

    const langCode = i18n.language || 'en';
  const currentLang = langCode.startsWith('hi') ? 'hi' : langCode.startsWith('mr') || langCode.startsWith('mar') ? 'mr' : 'en';
    const tLocal = farmerTranslations[currentLang];

    React.useEffect(() => {
        const fetchCrops = async () => {
            if (!userData?._id) return;
            try {
                const response = await fetch(`${url}/api/plots?user=${userData._id}`);
                const data = await response.json();
                if (data && data.paginatedPlots) {
                    const sortedCrops = data.paginatedPlots.sort((a, b) => {
                        // Sponsored/Boosted first (checking both possible spellings)
                        const aSpons = a.isSponsored || a.isSponsered;
                        const bSpons = b.isSponsored || b.isSponsered;
                        if (aSpons && !bSpons) return -1;
                        if (!aSpons && bSpons) return 1;
                        // Decaying order - Newest first
                        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
                    });
                    setCrops(sortedCrops.slice(0, 6)); // Show more recent listings
                }
            } catch (err) {
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchCrops();
    }, [userData]);

    const handleCropClick = (crop) => {
        setSelectedCrop(crop);
        setIsDetailsModalOpen(true);
    };

    return (
        <Box sx={{
            width: '100%',
            minHeight: '100vh',
            backgroundColor: colors.background_light,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pb: 8
        }}>
            <Box sx={{
                width: '100%',
                maxWidth: '1600px',
                px: 0,
                pt: { xs: 3, md: 4 },
                display: 'flex',
                flexDirection: 'column',
                gap: '32px'
            }}>

                {/* 1. Welcome Section */}
                <Box>
                    <Typography sx={{
                        fontFamily: fonts.manrope || fonts.poppins,
                        fontWeight: 700,
                        fontSize: { xs: '24px', md: '30px' },
                        lineHeight: '36px',
                        letterSpacing: '-0.75px',
                        color: colors.text_dark || '#171C17',
                        mb: '4px'
                    }}>
                        {tLocal.welcomeBack}
                    </Typography>
                    <Typography sx={{
                        fontFamily: fonts.publicSans || fonts.poppins,
                        fontWeight: 400,
                        fontSize: '16px',
                        lineHeight: '24px',
                        color: colors.text || '#6D786D'
                    }}>
                        {tLocal.manageProduce}
                    </Typography>
                </Box>

                {/* Wrapper to reverse order on mobile: Boost Banner first, then Action Cards */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', md: 'column' }, gap: '32px', width: '100%' }}>
                    {/* 2. Action Cards Row */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: '10px', sm: '12px' }, width: '100%' }}>
                        <ActionCard
                            icon={IconPlus}
                            iconBg={colors.primary}
                            title={tLocal.sellCrop}
                            onClick={() => onActionClick && onActionClick('sell')}
                        />
                        <ActionCard
                            icon={IconCube}
                            iconBg={'#3B82F6'}
                            title={tLocal.myListings}
                            onClick={() => onActionClick && onActionClick('listings')}
                        />
                        <ActionCard
                            icon={IconBolt}
                            iconBg={'#F59E0B'}
                            title={tLocal.boostPlans}
                            onClick={() => onActionClick && onActionClick('boost')}
                        />
                        <ActionCard
                            icon={IconLeaf}
                            iconBg={'#00B6E9'}
                            title={tLocal.carbonCredits}
                            onClick={() => onActionClick && onActionClick('carbon')}
                        />
                    </Box>

                    {/* 3. Boost Your Listings Banner */}
                    <Box sx={{
                        width: '100%',
                        height: { xs: 'auto', sm: '188px' },
                        minHeight: '188px',
                        borderRadius: '32px',
                        background: colors.boost_bg,
                        p: { xs: '24px 20px', sm: '32px 48px' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 3, sm: 0 },
                        color: '#FFFFFF'
                    }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '400px' }}>
                            <Typography sx={{
                                fontFamily: fonts.manrope || fonts.poppins,
                                fontWeight: 700,
                                fontSize: { xs: '24px', md: '30px' },
                                lineHeight: '36px',
                                letterSpacing: '-0.75px'
                            }}>
                                {tLocal.boostTextTitle}
                            </Typography>
                            <Typography sx={{
                                fontFamily: fonts.publicSans || fonts.poppins,
                                fontWeight: 400,
                                fontSize: '16px',
                                lineHeight: '24px',
                                opacity: 0.95
                            }}>
                                {tLocal.boostTextSub}
                            </Typography>
                            <Box sx={{ mt: '12px' }}>
                                <Box
                                    component="button"
                                    onClick={onViewPlansClick}
                                    sx={{
                                        height: '36px',
                                        padding: '8px 16px',
                                        borderRadius: '47px',
                                        backgroundColor: '#FFFFFF',
                                        color: '#F59E0B',
                                        border: 'none',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontFamily: fonts.poppins,
                                        fontWeight: 700,
                                        fontSize: '14px',
                                        cursor: 'pointer',
                                        boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
                                        transition: 'transform 0.2s',
                                        '&:hover': {
                                            transform: 'scale(1.05)'
                                        }
                                    }}
                                >
                                    {tLocal.viewPlans} <IconChevronRight size={16} stroke={2.5} />
                                </Box>
                            </Box>
                        </Box>

                        {/* Visual Illustration spacing (Optional space for an image if intended from design) */}
                        <Box sx={{ display: { xs: 'none', sm: 'block' }, flex: 1 }} />
                    </Box>
                </Box>

                {/* 4. My Listings Section */}
                <Box sx={{ width: '100%', mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '20px' }}>
                        <Typography sx={{
                            fontFamily: fonts.manrope || fonts.poppins,
                            fontWeight: 700,
                            fontSize: '20px',
                            lineHeight: '28px',
                            color: colors.text_dark || '#1C252E'
                        }}>
                            {tLocal.myListings}
                        </Typography>

                        <Typography
                            onClick={onViewAllClick}
                            sx={{
                                fontFamily: fonts.publicSans || fonts.poppins,
                                fontWeight: 600,
                                fontSize: '14px',
                                color: colors.primary,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                '&:hover': { textDecoration: 'underline' }
                            }}
                        >
                            {tLocal.viewAll} <IconChevronRight size={14} stroke={3} />
                        </Typography>
                    </Box>

                    {/* Listings Display or Empty State */}
                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress size={30} sx={{ color: colors.primary }} />
                        </Box>
                    ) : crops.length > 0 ? (
                        <Grid container spacing={3}>
                            {crops.map((crop) => (
                                <Grid item xs={12} sm={6} md={4} key={crop._id}>
                                    <UnifiedCropCard 
                                        crop={crop} 
                                        userData={userData}
                                        onDeleteSuccess={(id) => setCrops(prev => prev.filter(c => c._id !== id))}
                                        onEdit={() => onActionClick && onActionClick('listings')}
                                        onBoost={() => { /* Handled internally in popup */ }}
                                        onDetails={handleCropClick}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Box sx={{
                            width: '100%',
                            height: '234px',
                            borderRadius: '32px',
                            backgroundColor: colors.white_bg || '#FFFFFF',
                            border: `1px solid ${colors.dashboard_card_border || '#E0E5EB'}`,
                            boxShadow: '0px 1px 2px -1px rgba(0, 0, 0, 0.1), 0px 1px 3px 0px rgba(0, 0, 0, 0.1)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '12px'
                        }}>
                            <IconBox size={48} color={colors.text || '#6D786D'} stroke={1.5} style={{ opacity: 0.8 }} />

                            <Box sx={{ textAlign: 'center' }}>
                                <Typography sx={{
                                    fontFamily: fonts.poppins,
                                    fontWeight: 700,
                                    fontSize: '16px',
                                    lineHeight: '24px',
                                    color: colors.text_dark || '#000000',
                                    mb: '4px'
                                }}>
                                    {tLocal.noListingsYet}
                                </Typography>
                                <Typography sx={{
                                    fontFamily: fonts.poppins,
                                    fontWeight: 400,
                                    fontSize: '16px',
                                    lineHeight: '24px',
                                    color: colors.gray_empty_text || '#A6A6A6'
                                }}>
                                    {tLocal.startPosting}
                                </Typography>
                            </Box>

                            <Box
                                component="button"
                                onClick={onAddCropClick}
                                sx={{
                                    mt: '4px',
                                    padding: '8px 16px',
                                    borderRadius: '35px',
                                    backgroundColor: colors.primary,
                                    color: '#FFFFFF',
                                    border: 'none',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    fontFamily: fonts.poppins,
                                    fontWeight: 600,
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.1)',
                                    transition: 'background-color 0.2s',
                                    '&:hover': {
                                        backgroundColor: colors.primary_dark
                                    }
                                }}
                            >
                                <IconPlus size={16} stroke={2.5} /> {tLocal.addCrop}
                            </Box>
                        </Box>
                    )}
                </Box>
                <CropDetailsModal
                    open={isDetailsModalOpen}
                    onClose={() => setIsDetailsModalOpen(false)}
                    crop={selectedCrop}
                    userData={userData}
                    onGetContact={() => {}}
                />
            </Box>
        </Box>
    );
};

export default FarmerDashboard;
